/**
 * Wire schemas for the Operations **Settled book** surface — the Settled
 * book: cases that have settled and are being walked through disbursement
 * to Case Closed.
 *
 * A placement reaches **Settled** when the case settles and funds start
 * moving. Settled book is the inverse of the Placed book: where the Placed
 * book is the thickest, lowest-attention part of the pipeline (a census),
 * the Settled book is the attention-heavy stretch where the clock works
 * against you — checks have to reach the client, LF, and the OF per the FSA
 * split, the Handling Firm needs chasing, and the matter is tidied up before
 * it closes to Case Closed. It is a worklist, not a census.
 *
 * The data layer (2026-05-22): each card carries the disbursement read —
 * days-in-Settled (anchored on Settlement Date), the settlement amount,
 * whether the itemized disbursement doc is uploaded, and the three-gate
 * disbursement spine (HF disbursed → LF received → OF received). The list is
 * sorted worst-first (longest-in-Settled on top). Deliberately NOT here:
 * firm names and the fee split (the disbursement state is the job), and case
 * costs — a separate, deferred component Chris is standing up that plugs in
 * when a Transfer goes to Placed, earlier than here. See the port-note.
 *
 * The join: a Settled placement's lead stays at Lead Status "Placed"
 * (Settled is a Placement Decision, not a Lead Status). Verified live — all
 * 13 Settled placements' leads carry Status "Placed" — so the composer
 * fetches the Placed-status leads and keeps the placements at Decision
 * "Settled", the mirror of the Placed book join.
 *
 * All derivation is read-only and live — no stored Settled book state.
 *
 * Spec: engineering/command-center/docs/port-notes/home-stretch.md (evicted 2026-08-29, #775 round 10)
 */

import { z } from "zod";
import {
  PANEL_DETAIL_LEAD_FIELDS,
  PLACEMENT_DETAIL_FIELDS,
} from "./placement-detail.js";

/**
 * One case in the Settled book. The unit is the **placement** that settled
 * (Decision = Settled); its lead supplies the client identity. The composer
 * does the join so the UI renders without its own lookups.
 *
 * Each card carries the disbursement read: who's in the split (originating +
 * handling firm and their fee shares), how long it's been settling, the
 * money, and the three-gate disbursement spine — whether the HF has paid out,
 * and whether LF and the OF have each confirmed receipt. These are the fields
 * Chris reads to spot a matter that has stalled mid-disbursement.
 *
 * The fee split is normally three-party (HF + LF + OF, summing to 100%). When
 * LegalFlare is itself the Originating Firm (`originatingFirmIsLegalFlare`),
 * it is a two-party split: LF takes no separate platform cut (LF % is 0), and
 * the OF % is LegalFlare's referral share. The UI drops the LF line in that
 * case.
 */
export const SettledCaseSchema = z.object({
  /** The Settled placement's record ID — the case's identity here. */
  id: z.string(),
  /** Lead record ID — for joining to other surfaces / the future detail. */
  leadRecordId: z.string(),
  /** Stable human-readable Lead ID (Airtable primary text). */
  leadId: z.string(),
  /** Client name. */
  client: z.string(),

  /** Joined Originating Firm name (the OF that referred the matter), or null.
   *  When the OF is LegalFlare this reads "LegalFlare". */
  originatingFirmName: z.string().nullable(),
  /** Joined Handling Firm name (the firm that settled the case), or null. */
  handlingFirmName: z.string().nullable(),
  /** TRUE when the OF is LegalFlare itself — a two-party split. The UI drops
   *  the separate LF line and reads the OF % as LegalFlare's share. */
  originatingFirmIsLegalFlare: z.boolean(),
  /** HF share of the attorney-fee pool as a decimal (0.6 = 60%), or null. */
  hfFeePct: z.number().nullable(),
  /** LF (platform) share as a decimal, or null. Zero/absent on LF-as-OF. */
  lfFeePct: z.number().nullable(),
  /** OF share as a decimal, or null. Carries LegalFlare's cut when LF-as-OF. */
  ofFeePct: z.number().nullable(),

  /** Raw Settlement Date (ISO date) off the placement — HF-entered, the date
   *  the case settled. The age anchor. Null when unset (an anomaly for a
   *  Settled placement: the portal requires it to flip the case to Settled). */
  settlementDate: z.string().nullable(),
  /** SURFACE — days since the case settled (from Settlement Date), clamped
   *  ≥ 0. The worklist's worst-first axis. Null when Settlement Date is
   *  unset (days unknown — sorts last). */
  daysInSettled: z.number().nullable(),
  /** Settlement amount the HF reported, or null when not yet written. */
  settlementAmount: z.number().nullable(),
  /** HF's attorney-fee rate on this settlement as a decimal (0.33 = 33%),
   *  or null. Distinct from `hfFeePct` (the fee-pool split). Slice 5b
   *  surfaces this to the panel's edit-settlement form so the operator
   *  reads + amends the same value the portal does. Bounded by
   *  ATTORNEY_FEE_RATE_MAX (0.60). */
  attorneyFeeRate: z.number().nullable(),
  /** TRUE when the itemized settlement/disbursement document is uploaded. */
  hasSettlementDocument: z.boolean(),
  /** Date the Handling Firm confirmed it disbursed funds to LF + OF, or
   *  null. Presence = the firm acknowledged the payout. */
  hfDisbursementConfirmed: z.string().nullable(),
  /** Date LegalFlare confirmed it received its share, or null. */
  lfPaymentDate: z.string().nullable(),
  /** Date the Originating Firm confirmed it received its share, or null. */
  ofPaymentDate: z.string().nullable(),

  // Panel-detail lead-context fields (Aesthetic Parity, Phase 2) — the joined
  // lead's transfer flag, received date, case file, and FSA document, defined
  // once in PANEL_DETAIL_LEAD_FIELDS. Additive (ADR-0133).
  ...PANEL_DETAIL_LEAD_FIELDS,

  // Class-B placement fields (slice 4 — panel detail). The Settled book
  // still cares about both — monthly updates continue until close, and
  // an outstanding filing deadline can block disbursement.
  ...PLACEMENT_DETAIL_FIELDS,
});
export type SettledCase = z.infer<typeof SettledCaseSchema>;

/**
 * Payload for `GET /api/settled`. `cases` is the full Settled book,
 * sorted worst-first (longest-in-Settled on top) so the matters that have
 * stalled longest in disbursement surface first. Cases with no Settlement
 * Date (days unknown) sort last.
 */
export const SettledResponseSchema = z.object({
  cases: z.array(SettledCaseSchema),
});
export type SettledResponse = z.infer<typeof SettledResponseSchema>;
